/**
 * Audit trail timestamps are stored in the database as UTC (MySQL DATETIME).
 * API may return `YYYY-MM-DD HH:mm:ss` or ISO strings — always treat as UTC, display in local time.
 */

export function parseAuditTrailUtcDate(value: string | Date | null | undefined): Date | null {
  if (value == null) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const trimmed = String(value).trim();
  if (!trimmed) return null;

  if (trimmed.includes('T')) {
    const hasZone = trimmed.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(trimmed);
    const d = new Date(hasZone ? trimmed : `${trimmed}Z`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(`${trimmed.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function toAuditTrailUtcMillis(value: string | null | undefined): number {
  const d = parseAuditTrailUtcDate(value);
  return d ? d.getTime() : 0;
}

export function formatAuditTrailDateTime(value: string | null | undefined): string {
  const d = parseAuditTrailUtcDate(value);
  if (!d) return '—';
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function formatAuditTrailDateOnly(value: string | null | undefined): string {
  const d = parseAuditTrailUtcDate(value);
  if (!d) return '—';
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function formatAuditTrailTimeOnly(value: string | null | undefined): string {
  const d = parseAuditTrailUtcDate(value);
  if (!d) return '—';
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatAuditTrailTimeWithSeconds(value: string | null | undefined): string {
  const d = parseAuditTrailUtcDate(value);
  if (!d) return '—';
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}
